/**
 * @typedef {Object} ParticipantSelection
 * @property {string}  tenantId     - tenant_info._id of the selected participant
 * @property {string[]} guarantorIds - (future) selected guarantor IDs for this participant
 */

/**
 * @typedef {Object} TenantEntry
 * @property {string}  type            - "tenant"
 * @property {string}  sharedProfileId - _id of the parent shared-profile document
 * @property {string}  tenantId        - tenantid._id of the direct tenant
 * @property {string[]} guarantorIds   - (future) selected guarantor IDs
 */

/**
 * @typedef {Object} ApplicationEntry
 * @property {string}               type            - "application"
 * @property {string}               sharedProfileId - _id of the parent shared-profile document
 * @property {string}               applicationId   - application_id._id
 * @property {ParticipantSelection[]} participants   - individually selected participants
 */

/**
 * @typedef {(TenantEntry | ApplicationEntry)[]} SelectionModel
 */

// ---------------------------------------------------------------------------
// Pure query helpers — read-only, no side effects
// ---------------------------------------------------------------------------

/**
 * Returns a flat array of all currently selected tenant/participant IDs
 * across the entire selection model.
 *
 * @param {SelectionModel} model
 * @returns {string[]}
 */
export const getSelectedTenantIds = (model) =>
  model.flatMap((entry) => {
    if (entry.type === "tenant") return [entry.tenantId].filter(Boolean);
    return (entry.participants || []).map((p) => p.tenantId).filter(Boolean);
  });

/**
 * Builds a stable cache key for an insurance simulation selection. The API
 * payload includes guarantors, so they must be part of the key as well as the
 * selected tenants and rent.
 *
 * @param {Object[]} selections - API `selections` shape
 * @param {number|string} rent
 * @returns {string}
 */
export const getSimulationCacheKey = (selections = [], rent) => {
  const normaliseGuarantors = (guarantors = []) =>
    [...guarantors].filter(Boolean).map(String).sort();

  const normalisedSelections = selections
    .map((selection) => {
      if (selection?.tenant) {
        return {
          tenant: String(selection.tenant),
          guarantors: normaliseGuarantors(selection.guarantors),
        };
      }

      if (selection?.application) {
        return {
          application: String(selection.application),
          participants: (selection.participants || [])
            .map((participant) => ({
              tenant: String(participant.tenant),
              guarantors: normaliseGuarantors(participant.guarantors),
            }))
            .sort((a, b) => a.tenant.localeCompare(b.tenant)),
        };
      }

      return null;
    })
    .filter(Boolean)
    .sort((a, b) =>
      (a.tenant || a.application).localeCompare(b.tenant || b.application)
    );

  return `${JSON.stringify(normalisedSelections)}__${rent}`;
};

/**
 * Rebuilds the selected shared-profile objects from the API `selections`
 * structure. Application participants are filtered to the exact participants
 * in the selection so downstream insurance and display code cannot
 * accidentally treat the whole application as selected.
 *
 * @param {Object[]} selections
 * @param {Object[]} sharedProfiles
 * @param {Object} [options]
 * @param {boolean} [options.filterParticipants=true]
 * @returns {Object[]}
 */
export const getSelectedProfilesFromSelections = (
  selections = [],
  sharedProfiles = [],
  { filterParticipants = true } = {}
) =>
  selections
    .map((selection) => {
      if (selection?.tenant) {
        return sharedProfiles.find(
          (profile) =>
            String(profile?.tenantid?._id || profile?.tenant_id?._id) ===
            String(selection.tenant)
        );
      }

      if (selection?.application) {
        const sharedProfile = sharedProfiles.find(
          (profile) =>
            String(profile?.application_id?._id) ===
            String(selection.application)
        );
        if (!sharedProfile) return null;
        if (!filterParticipants) return sharedProfile;

        const participantIds = new Set(
          (selection.participants || []).map((participant) =>
            String(participant?.tenant)
          )
        );

        return {
          ...sharedProfile,
          application_id: {
            ...sharedProfile.application_id,
            participants: (
              sharedProfile.application_id?.participants || []
            ).filter((participant) =>
              participantIds.has(
                String(participant?.tenant_info?._id || participant?.id)
              )
            ),
          },
        };
      }

      return null;
    })
    .filter(Boolean);

/**
 * Reconstructs the UI selection model from persisted API selections.
 *
 * @param {Object[]} selections
 * @param {Object[]} sharedProfiles
 * @returns {SelectionModel}
 */
export const getSelectionModelFromSelections = (
  selections = [],
  sharedProfiles = []
) =>
  selections
    .map((selection) => {
      if (selection?.tenant) {
        const sharedProfile = sharedProfiles.find(
          (profile) =>
            String(profile?.tenantid?._id || profile?.tenant_id?._id) ===
            String(selection.tenant)
        );
        if (!sharedProfile) return null;
        return {
          sharedProfileId: sharedProfile._id,
          type: "tenant",
          tenantId: selection.tenant,
          guarantorIds: selection.guarantors || [],
        };
      }

      if (selection?.application) {
        const sharedProfile = sharedProfiles.find(
          (profile) =>
            String(profile?.application_id?._id) ===
            String(selection.application)
        );
        if (!sharedProfile) return null;
        return {
          sharedProfileId: sharedProfile._id,
          type: "application",
          applicationId: selection.application,
          participants: (selection.participants || []).map((participant) => ({
            tenantId: participant.tenant,
            guarantorIds: participant.guarantors || [],
          })),
        };
      }

      return null;
    })
    .filter(Boolean);

/**
 * Converts selected shared-profile objects to the API `selections` shape.
 * Primarily used as a compatibility bridge for records that were persisted
 * with the former `selected_tenants` / `application_id` fields.
 *
 * @param {Object[]} sharedProfiles
 * @returns {Object[]}
 */
export const getSelectionsFromProfiles = (sharedProfiles = []) =>
  sharedProfiles
    .map((profile) => {
      if (profile?.application_id?._id) {
        return {
          application: profile.application_id._id,
          participants: (profile.application_id.participants || [])
            .map((participant) => ({
              tenant: participant?.tenant_info?._id || participant?.id,
              guarantors:
                participant?.tenant_info?.gurantor_id?.map(
                  (guarantor) => guarantor?._id
                ) || [],
            }))
            .filter((participant) => participant.tenant),
        };
      }

      const tenant = profile?.tenantid || profile?.tenant_id;
      if (tenant?._id) {
        return {
          tenant: tenant._id,
          guarantors:
            tenant?.gurantor_id?.map((guarantor) => guarantor?._id) || [],
        };
      }

      return null;
    })
    .filter(Boolean);

/**
 * Returns the shared-profile `_id` values for every entry in the model.
 * These are used to build the `sharedprofileid` query params for the report
 * page and to call `getTenantExtraData`.
 *
 * @param {SelectionModel} model
 * @returns {string[]}
 */
export const getSelectedSharedProfileIds = (model) =>
  model.map((entry) => entry.sharedProfileId).filter(Boolean);

/**
 * Checks whether a specific participant (by `tenant_info._id`) is selected
 * inside a given application's entry in the model.
 *
 * @param {SelectionModel} model
 * @param {string} sharedProfileId - parent shared-profile _id
 * @param {string} participantId   - participant's tenant_info._id
 * @returns {boolean}
 */
export const isParticipantSelected = (model, sharedProfileId, participantId) =>
  model.some(
    (entry) =>
      entry.sharedProfileId === sharedProfileId &&
      entry.type === "application" &&
      (entry.participants || []).some((p) => p.tenantId === participantId)
  );

/**
 * Returns `true` when every participant belonging to an application entry
 * is selected in the model. Used to determine the application-level
 * checkbox's checked state.
 *
 * @param {SelectionModel} model
 * @param {string} sharedProfileId
 * @param {Object} entry - the full shared-profile entry (same shape as items in propertyShareProfiles)
 * @returns {boolean}
 */
export const isApplicationFullySelected = (model, sharedProfileId, entry) => {
  const appEntry = model.find(
    (e) => e.sharedProfileId === sharedProfileId && e.type === "application"
  );
  if (!appEntry) return false;
  const allParticipantIds = (entry?.application_id?.participants || [])
    .map((p) => p?.tenant_info?._id)
    .filter(Boolean);
  if (allParticipantIds.length === 0) return false;
  const selectedIds = (appEntry.participants || []).map((p) => p.tenantId);
  return allParticipantIds.every((id) => selectedIds.includes(id));
};

/**
 * Returns `true` when at least one (but not all) participants of an
 * application entry are selected. Used for the indeterminate (—) checkbox
 * state on the application header.
 *
 * @param {SelectionModel} model
 * @param {string} sharedProfileId
 * @param {Object} entry - the full shared-profile entry
 * @returns {boolean}
 */
export const isApplicationPartiallySelected = (
  model,
  sharedProfileId,
  entry
) => {
  const appEntry = model.find(
    (e) => e.sharedProfileId === sharedProfileId && e.type === "application"
  );
  if (!appEntry) return false;
  const allParticipantIds = (entry?.application_id?.participants || [])
    .map((p) => p?.tenant_info?._id)
    .filter(Boolean);
  if (allParticipantIds.length === 0) return false;
  const selectedIds = (appEntry.participants || []).map((p) => p.tenantId);
  const hasSelected = allParticipantIds.some((id) => selectedIds.includes(id));
  const allSelected = allParticipantIds.every((id) => selectedIds.includes(id));
  return hasSelected && !allSelected;
};

/**
 * Returns `true` when the number of entries in the model equals the total
 * number of available shared-profile entries. Used by the top-level
 * "Select All" checkbox.
 *
 * @param {SelectionModel} model
 * @param {number} totalEntries - length of propertyShareProfiles
 * @returns {boolean}
 */
export const areAllEntriesSelected = (model, totalEntries) =>
  model.length === totalEntries && totalEntries > 0;

/**
 * Returns a flat array of all currently selected guarantor IDs across the
 * entire selection model.
 *
 * @param {SelectionModel} model
 * @returns {string[]}
 */
export const getSelectedGuarantorIds = (model) =>
  model.flatMap((entry) => {
    if (entry.type === "tenant") return entry.guarantorIds || [];
    return (entry.participants || []).flatMap((p) => p.guarantorIds || []);
  });

/**
 * Returns the selected guarantor IDs scoped to a specific tenant or
 * application participant, rather than a global flat list.
 *
 * @param {SelectionModel} model
 * @param {string} sharedProfileId - _id of the shared-profile document
 * @param {string} [tenantId]      - tenant / participant ID (required for
 *   application entries to locate the correct participant)
 * @returns {string[]}
 */
export const getGuarantorIdsForEntry = (model, sharedProfileId, tenantId) => {
  const entry = model.find((e) => e.sharedProfileId === sharedProfileId);
  if (!entry) return [];
  if (entry.type === "tenant") return entry.guarantorIds || [];
  if (entry.type === "application") {
    const participant = entry.participants?.find(
      (p) => p.tenantId === tenantId
    );
    return participant?.guarantorIds || [];
  }
  return [];
};
